import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useLoadingStore } from "../Store/store";
import axios from "axios";
import { openDB } from "idb";
import {
  Container,
  FormCard,
  FormTitle,
  FormField,
  Label,
  Input,
  TextArea,
  RadioGroup,
  RadioOption,
  FileUpload,
  FileList,
  FileItem,
  RemoveButton,
  ButtonGroup,
  CancelButton,
  CreateButton,
} from "./CreatePage/CreatePage.styles";
import {
  completeUpload,
  createProject,
  createProjectTask,
  createProjectTaskToDB,
  fileUploadChunk,
  fileUploadInit,
} from "../api/ProjectAPI";
import { guessCRS } from "../components/PotreeUtil/guessCRS";

export default function TaskCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    projectName: "",
    projectDescription: "",
    viewPermission: "COMPANY_INTERNAL",
    files: "",
  });
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    progress: 0,
    fileId: null,
    totalChunks: 0,
    uploadedChunks: 0,
    chunkSize: 0,
    status: "idle", // idle, initializing, uploading, completed, failed
    error: null,
  });
  const { isLoading, showLoading, hideLoading } = useLoadingStore();

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileUpload = (files) => {
    if (files.length > 0) {
      const file = files[0]; // 첫 번째 파일만 사용
      const newFile = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        file: file,
      };

      setFormData((prev) => ({
        ...prev,
        files: [newFile], // 기존 파일들을 대체
      }));
    }
  };

  const removeFile = (fileId) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((file) => file.id !== fileId),
    }));
  };

  // 파일 저장
  const saveFileToIndexedDB = async (file) => {
    const db = await openDB("fileStorage", 1, {
      upgrade(db) {
        db.createObjectStore("files");
      },
    });

    await db.put("files", file, "currentFile");
  };

  const handleSubmit = async (e) => {
    // 파일 이름과 파일 크기 가져오기
    const fileName = formData.files[0].name;
    const fileSize = formData.files[0].size;
    e.preventDefault();

    const formDataToSend = new FormData();
    formDataToSend.append("projectName", formData.projectName);
    formDataToSend.append("projectDescription", formData.projectDescription);
    formDataToSend.append("viewPermission", formData.viewPermission);

    // 파일 추가
    formData.files.forEach(async (fileObj) => {
      formDataToSend.append("file", fileObj.file);
      await saveFileToIndexedDB(fileObj.file);
    });

    // 이후에 별도로 로직 분리 예정 ------------------------------
    // 파일을 청크로 분할하는 함수
    const createFileChunks = (file, chunkSize) => {
      const chunks = [];
      let offset = 0;
      let chunkNumber = 0;

      while (offset < file.size) {
        const end = Math.min(offset + chunkSize, file.size);
        const chunk = file.slice(offset, end);

        chunks.push({
          number: chunkNumber,
          data: chunk,
          size: chunk.size,
          offset: offset,
        });

        offset = end;
        chunkNumber++;
      }

      return chunks;
    };

    try {
      showLoading("태스크 생성 중...");
      const file = formDataToSend.get("file");

      // 백엔드 요청 (파일 이니셜라이징) 여기서 파일 아이디를 받는다.
      const fileUploadResponse = await fileUploadInit(fileName, fileSize);

      const fileId = fileUploadResponse.fileId;
      const chunkSize = fileUploadResponse.chunkSize;
      localStorage.setItem("fileId", fileId);

      // 파일을 청크로 분할
      const chunks = createFileChunks(file, chunkSize);

      let uploadedCount = 0;
      for (const chunk of chunks) {
        await fileUploadChunk(fileId, chunk.number, chunk.data);
        uploadedCount++;

        // 진행률 업데이트
        const progress = Math.round((uploadedCount / chunks.length) * 100);
        setUploadState((prev) => ({
          ...prev,
          progress,
          uploadedChunks: uploadedCount,
        }));

        showLoading(
          `파일 업로드 중... ${uploadedCount}/${chunks.length} 조각 (${progress}%)`,
        );
      }

      showLoading("파일 저장 중...");
      const response = await completeUpload(fileId);

      const { bucketName, objectName, outputBucket, outputObject } = response;
      localStorage.setItem("outputBucket", outputBucket);
      localStorage.setItem("outputObject", outputObject);
      localStorage.setItem("bucketName", bucketName);
      localStorage.setItem("objectName", objectName);

      showLoading("파일 분석 중...");
      // 모델 처리
      const resp = await createProjectTask(bucketName, objectName);

      const zStat = resp?.stats?.stats?.statistic?.find(
        (s) => s?.name?.toUpperCase() === "Z",
      );
      const xStat = resp?.stats?.stats?.statistic?.find(
        (s) => s?.name?.toUpperCase() === "X",
      );
      const yStat = resp?.stats?.stats?.statistic?.find(
        (s) => s?.name?.toUpperCase() === "Y",
      );

      // 좌표계 추측
      const EPSG = guessCRS(xStat.average, yStat.average);

      localStorage.setItem("EPSG", JSON.stringify(EPSG));

      if (zStat) {
        const result = {
          count: zStat.count,
          minZ: zStat.minimum,
          maxZ: zStat.maximum,
        };
        localStorage.setItem("pdal_stats_Z", JSON.stringify(result));
      } else {
      }
      const resp2 = await createProjectTaskToDB(formData, fileId, id);

      hideLoading();
      navigate(`/quality-check/${id}`);
    } catch (error) {
      console.error("오류:", error);
      hideLoading();
      alert("태스크 생성에 실패했습니다.");
    }
  };

  const handleCancel = () => {
    navigate("/project");
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Container>
      <FormCard>
        <FormTitle>태스크 생성</FormTitle>

        <form onSubmit={handleSubmit}>
          <FormField>
            <Label>태스크 이름</Label>
            <Input
              type="text"
              placeholder="태스크 이름을 입력하세요"
              value={formData.projectName}
              onChange={(e) => handleInputChange("projectName", e.target.value)}
              required
            />
          </FormField>

          <FormField>
            <Label>태스크 설명</Label>
            <TextArea
              placeholder="태스크에 대한 설명을 입력하세요"
              value={formData.projectDescription}
              onChange={(e) =>
                handleInputChange("projectDescription", e.target.value)
              }
              required
            />
          </FormField>

          <FormField>
            <Label>열람 권한 설정</Label>
            <RadioGroup>
              <RadioOption>
                <input
                  type="radio"
                  name="viewPermission"
                  value="PUBLIC"
                  checked={formData.viewPermission === "PUBLIC"}
                  onChange={(e) =>
                    handleInputChange("viewPermission", e.target.value)
                  }
                />
                <span>전체 공개 (모든 사용자가 열람 가능)</span>
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  name="viewPermission"
                  value="COMPANY_INTERNAL"
                  checked={formData.viewPermission === "COMPANY_INTERNAL"}
                  onChange={(e) =>
                    handleInputChange("viewPermission", e.target.value)
                  }
                />
                <span>회사 내부 (회사 내부 사용자만 열람 가능)</span>
              </RadioOption>
            </RadioGroup>
          </FormField>

          <FormField>
            <Label>파일 업로드</Label>
            <FileUpload
              onClick={() => document.getElementById("file-input").click()}
            >
              <p>클릭하여 파일을 선택하세요</p>
              <p style={{ fontSize: "12px", color: "#999", marginTop: "8px" }}>
                지원 형식: .las, .pcd, .shp
              </p>
              <input
                id="file-input"
                type="file"
                accept=" .las, .pcd, .shp"
                onChange={(e) => handleFileUpload(e.target.files)}
                style={{ display: "none" }}
              />
            </FileUpload>

            {formData.files.length > 0 && (
              <FileList>
                {formData.files.map((file) => (
                  <FileItem key={file.id}>
                    <span>
                      {file.name} ({formatFileSize(file.size)})
                    </span>
                    <RemoveButton onClick={() => removeFile(file.id)}>
                      삭제
                    </RemoveButton>
                  </FileItem>
                ))}
              </FileList>
            )}
          </FormField>

          <ButtonGroup>
            <CancelButton type="button" onClick={handleCancel}>
              취소
            </CancelButton>
            <CreateButton type="submit">태스크 생성</CreateButton>
          </ButtonGroup>
        </form>
      </FormCard>
    </Container>
  );
}
